import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { configService } from "../services/configService";
import { RefreshCw, ChevronDown, ChevronUp } from "lucide-react";
import { validateCronExpression, describeCronExpression } from "../utils/cronUtils";
import { isTokenInvalidationError, isLoggingOut } from "../utils/errorUtils";

const SupplierShipmentScheduler: React.FC = () => {
  const [cronExpression, setCronExpression] = useState<string>("0 10 * * *");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  
  // Dropdown states for cron components
  const [minute, setMinute] = useState<string>("*");
  const [hour, setHour] = useState<string>("*");
  const [day, setDay] = useState<string>("*");
  const [month, setMonth] = useState<string>("*");
  const [weekDay, setWeekDay] = useState<string>("*");

  // Generate options for dropdowns
  const minuteOptions = [
    { value: "*", label: "Every minute" },
    { value: "0", label: "0" },
    { value: "15", label: "15" },
    { value: "30", label: "30" },
    { value: "45", label: "45" },
    { value: "*/5", label: "Every 5 minutes" },
    { value: "*/10", label: "Every 10 minutes" },
    { value: "*/15", label: "Every 15 minutes" },
    { value: "*/30", label: "Every 30 minutes" }
  ];

  const hourOptions = [
    { value: "*", label: "Every hour" },
    ...Array.from({ length: 24 }, (_, i) => ({ value: i.toString(), label: `${i}:00` })),
    { value: "*/2", label: "Every 2 hours" },
    { value: "*/6", label: "Every 6 hours" },
    { value: "*/12", label: "Every 12 hours" }
  ];

  const dayOptions = [
    { value: "*", label: "Every day" },
    ...Array.from({ length: 31 }, (_, i) => ({ value: (i + 1).toString(), label: (i + 1).toString() })),
    { value: "*/2", label: "Every 2 days" },
    { value: "*/7", label: "Every 7 days" }
  ];

  const monthOptions = [
    { value: "*", label: "Every month" },
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" }
  ];

  const weekDayOptions = [
    { value: "*", label: "Every day" },
    { value: "0", label: "Sunday" },
    { value: "1", label: "Monday" },
    { value: "2", label: "Tuesday" },
    { value: "3", label: "Wednesday" },
    { value: "4", label: "Thursday" },
    { value: "5", label: "Friday" },
    { value: "6", label: "Saturday" },
    { value: "1-5", label: "Weekdays (Mon-Fri)" },
    { value: "0,6", label: "Weekends (Sat-Sun)" }
  ];

  // Update cron expression when dropdowns change
  useEffect(() => {
    const newCron = `${minute} ${hour} ${day} ${month} ${weekDay}`;
    setCronExpression(newCron);
  }, [minute, hour, day, month, weekDay]);

  // Parse existing cron expression into dropdown values
  const parseCronExpression = (cron: string) => {
    const parts = cron.split(' ');
    if (parts.length === 5) {
      setMinute(parts[0]);
      setHour(parts[1]);
      setDay(parts[2]);
      setMonth(parts[3]);
      setWeekDay(parts[4]);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        const schedule = await configService.getSupplierShipmentSyncSchedule();
        if (Array.isArray(schedule) && schedule.length > 0) {
          const firstCron = schedule[0];
          setCronExpression(firstCron);
          parseCronExpression(firstCron);
        }
      } catch (error) {
        console.error("Failed to load supplier shipment sync schedule:", error);
        // Don't show error if it's a token invalidation (interceptor will handle it)
        if (!isTokenInvalidationError(error) && !isLoggingOut()) {
          toast.error("Failed to load sync schedule");
        }
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const saveSchedule = async () => {
    const validation = validateCronExpression(cronExpression);
    
    if (!validation.isValid) {
      toast.error(validation.error || "Invalid cron expression");
      return;
    }

    try {
      setIsSaving(true);
      await configService.updateSupplierShipmentSyncSchedule([cronExpression]);
      toast.success("Supplier shipment sync schedule updated successfully");
    } catch (error) {
      console.error("Failed to save supplier shipment sync schedule:", error);
      // Don't show error if it's a token invalidation (interceptor will handle it)
      if (!isTokenInvalidationError(error) && !isLoggingOut()) {
        toast.error("Failed to save sync schedule");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const renderDropdown = (
    label: string,
    value: string,
    onChange: (value: string) => void,
    options: { value: string; label: string }[]
  ) => (
    <div className="flex flex-col space-y-1">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center space-x-2">
          <RefreshCw className="h-5 w-5 text-green-600 animate-spin" />
          <h3 className="text-lg font-semibold text-gray-800">Sync Scheduler</h3>
        </div>
        {/* <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-10 bg-gray-200 rounded mb-4"></div>
        </div> */}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <RefreshCw className="h-5 w-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-800">Sync Scheduler</h3>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Accordion content */}
      {isExpanded && (
        <div className="space-y-4 mt-4">
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800">
              Configure when automatic supplier shipment synchronization should occur. Select values for each time component to create your schedule.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {renderDropdown("minute (s)*", minute, setMinute, minuteOptions)}
            {renderDropdown("hour (s)*", hour, setHour, hourOptions)}
            {renderDropdown("day (s)*", day, setDay, dayOptions)}
            {renderDropdown("month (s)*", month, setMonth, monthOptions)}
            {renderDropdown("weekDay (s)*", weekDay, setWeekDay, weekDayOptions)}
          </div>

          {/* Current schedule display - always visible */}
          <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
            <div>
              <p className="text-sm font-medium text-gray-700">Current Schedule:</p>
              <p className="text-sm text-gray-600 font-mono">{cronExpression}</p>
              <p className="text-sm text-primary-600 mt-1">
                {describeCronExpression(cronExpression)}
              </p>
            </div>
          </div>

          {/* Save button */}
          <div className="flex justify-end">
            <button
              onClick={saveSchedule}
              disabled={isSaving}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSaving ? "Saving..." : "Save Schedule"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierShipmentScheduler;